import { Component, EventEmitter, Inject, Output, OnInit } from '@angular/core';
import { Subject } from 'rxjs';

import { UserService } from '../../../humain/user/services/user.service';
import { EntrepriseService } from '../../../entreprises/services/entreprise.service';

import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';
import { AgenceTLR } from '../../../structure/agence/entities/agence-tlr.entity';
import { PdpSearchCriteria } from '../../entities/plan-prevention-search-criteria';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { GroupementTLR } from '../../../structure/groupement-parc/entities/groupement-tlr.entity';
import { ParcWithGroupementDTO } from '../../../structure/parc/entities/parc-with-groupement-dto.entity';

export enum TypePdp {
	GENERAL = 0,
	SPECIFIQUE = 1
}
export enum EtatPdp {
	BROUILLON = 0,
	A_FAIRE_SIGNER = 1,
	ENVOYER_A_DOCUSIGN = 2,
	SIGNE_PARTIELLEMENT = 3,
	SIGNE = 4,
	ARCHIVER = 5,
	ATTENTE_DE_RELECTURE = 6, 
	INVALIDE = 7,
}
export enum ValiditePdp {
	EN_COURS = 0,
	EXPIRE = 1,
	ANNULE = 2
}

@Component({
	selector: 'pdp-search',
	templateUrl: 'pdp-search.html',
	styleUrls: ['pdp-search.css'],
})
export class PdpSearchComponent implements OnInit {
	
	user!: UserTLR;
	userRole: string = '';
	entreprises !: EntrepriseTLR[];
	searchCriteria: PdpSearchCriteria = new PdpSearchCriteria();
	getEntrepriseName = (entreprise: EntrepriseTLR) => entreprise.nom;
	typePdpOptions = [TypePdp.GENERAL, TypePdp.SPECIFIQUE];
	etatOptions = [EtatPdp.BROUILLON, EtatPdp.A_FAIRE_SIGNER, EtatPdp.ENVOYER_A_DOCUSIGN, EtatPdp.SIGNE_PARTIELLEMENT, EtatPdp.SIGNE, EtatPdp.ARCHIVER, EtatPdp.ATTENTE_DE_RELECTURE, EtatPdp.INVALIDE];
	validiteOptions = [ValiditePdp.EN_COURS, ValiditePdp.EXPIRE];
	perimetreReferent?: boolean;
	
	resetCommands = new Subject<void>();

	// Emetteur d'evenement pour la demande d'action sur un item
	@Output() onApplyFilters = new EventEmitter<PdpSearchCriteria>();

	private typePdpLabels: string[] = ["Général", "Spécifique"];
	private etatPdpLabels: string[] = ["Brouillon", "A faire signer", "Envoyé via DocuSign", "Signé partiellement", "Signé", "Archivé", "Attente de relecture", "Invalide"];
	private validiteLabels: string[] = ["En cours", "Expiré"];
	
	getTypePdpLabel = (typePdp: TypePdp) => this.typePdpLabels[typePdp];
	getEtatLabel = (etatPdp: EtatPdp) => this.etatPdpLabels[etatPdp];
	getValiditeLabel = (validitePdp: ValiditePdp) => this.validiteLabels[validitePdp];

	typePdpShouldBeSelectedByDefaut = (_typePdp: TypePdp) => true;
	etatPdpShouldBeSelectedByDefaut = (etatPdp: EtatPdp) => etatPdp != EtatPdp.ARCHIVER; // par défaut les archivés ne sont pas affichés
	validitePdpShouldBeSelectedByDefaut = (_validitePdp: ValiditePdp) => true;
	
	constructor(
		@Inject(UserService) private userService: UserService,
		@Inject(EntrepriseService) private entrepriseService: EntrepriseService
	) { }

	ngOnInit() {
		this.initForm(false);
	}
	
	initForm(shouldApplyFilter: boolean) {
		this.searchCriteria = new PdpSearchCriteria();
		this.userService.getCurrentUserTLR().subscribe(
			(user: UserTLR) => {
				if (user) {
					this.userRole = user.role;
					this.user = user;
					if (user.role === 'EXP' && user.antenneId) {
						this.searchCriteria.antenne = user.antenneId;
					}
					let entreprises$ = this.entrepriseService.getActiveEntreprisesTLRList();
					entreprises$.subscribe(
						(entreprises: EntrepriseTLR[]) => {
							this.entreprises = entreprises.filter( $ => $.documentApplicable === 'PlanPrevention' || $.documentApplicable === 'Both');
							if (shouldApplyFilter) {
								this.searchCriteria.typePdp = [0, 1];
								this.searchCriteria.etat = [0, 1, 2, 3, 4, 6, 7];
								this.searchCriteria.validite = [0, 1];
								this.applyfilters();
							}
						}
					);
				}
			}
		);
	}
	
	onPerimetreReferentChange(selected: boolean) {
		this.applyfilters();
	}
	
	onEntrepriseSelected(entreprise: EntrepriseTLR) {
		if (entreprise) {
			this.searchCriteria.entreprise = Number(entreprise.id);
			this.applyfilters();
		}
	}

	onTypePdpSelected(value: [number[], boolean]) {
		this.searchCriteria.typePdp = value[0];
		this.applyfilters();
	}

	onEtatPdpSelected(value: [number[], boolean]) {
		this.searchCriteria.etat = value[0];
		this.applyfilters();
	}

	onValiditePdpSelected(value: [number[], boolean]) {
		this.searchCriteria.validite = value[0];
		this.applyfilters();
	}
	
	onAgenceChange(selectedAgence: AgenceTLR | null) {
		if (selectedAgence) {
			if (selectedAgence.id != this.searchCriteria.agence) {
				// on a changé d'agence
				this.searchCriteria.agence = selectedAgence.id;
				// on vide l'antenne et le parc
				delete this.searchCriteria.antenne;
				delete this.searchCriteria.groupement;
				delete this.searchCriteria.parc;
			}
		} else {
			// on a cliquer sur moins pour enlever l'agence choisi
			delete this.searchCriteria.agence;
			delete this.searchCriteria.antenne;
			delete this.searchCriteria.groupement;
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}

	onAntenneChange(selectedAntenne: AntenneTLR | null) {
		if (selectedAntenne) {
			if (this.searchCriteria.agence === undefined) {
				// on a choisis un parc une antenne mais pas d'agence
				this.searchCriteria.agence = selectedAntenne.agenceId;
			}
			if (selectedAntenne.id != this.searchCriteria.antenne) {
				// on a changé d'antenne, on vide le parc
				this.searchCriteria.antenne = selectedAntenne.id;
				delete this.searchCriteria.parc;
			}
		} else {
			// on a cliquer sur moins pour enlever l'antenne choisi
			delete this.searchCriteria.antenne;
			delete this.searchCriteria.groupement;
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}
	
	onGroupementChange(selectedGroupement: GroupementTLR | null) {
		if (selectedGroupement) {
			this.searchCriteria.groupement = selectedGroupement.id;
			if (this.searchCriteria.antenne === undefined) {
				this.searchCriteria.antenne = selectedGroupement.antenneId;
			}
		} else {
			// Cas ou l'utilisateur a supprime le groupement selectionne
			delete this.searchCriteria.groupement;
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}

	onParcChange(selectedParc: ParcWithGroupementDTO | null) {
		if (selectedParc) {
			this.searchCriteria.parc = selectedParc.id;
			if (this.searchCriteria.antenne === undefined) {
				this.searchCriteria.antenne = selectedParc.antenneId;
			}
			if (this.searchCriteria.groupement === undefined) {
				this.searchCriteria.groupement = selectedParc.groupementId;
			}
		} else {
			// on a cliquer sur moins pour enlever le parc choisi
			delete this.searchCriteria.parc;
		}
		this.applyfilters();
	}
	
	onNumeroPdpChange() {
		this.applyfilters();
	}

	resetfilters() {
		this.resetCommands.next();
		this.initForm(true);
	}

	applyfilters() {
		// on filtre les acces en fonction des criteres choisis
		this.onApplyFilters.emit(this.searchCriteria);
	}

}